export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Checks for strict intersection between two axis-aligned rectangles. */
export const rectsOverlap = (a: Rect, b: Rect): boolean =>
  a.x < b.x + b.width &&
  a.x + a.width > b.x &&
  a.y < b.y + b.height &&
  a.y + a.height > b.y;

export interface FieldRectEntry<TPage = unknown> {
  id: string;
  boxRect: Rect;
  tooltipRect: Rect | null;
  page: TPage;
}

/**
 * Returns the fields whose tooltip overlaps another tooltip or field box on
 * the same page. Tooltip-to-tooltip collisions mark both fields; a tooltip
 * covering another field marks only the tooltip's field.
 */
export const computeOverlappingIndices = <TPage>(
  fieldRects: FieldRectEntry<TPage>[]
): Set<string> => {
  const overlapping = new Set<string>();
  const byPage = new Map<TPage, FieldRectEntry<TPage>[]>();

  for (const entry of fieldRects) {
    const group = byPage.get(entry.page) ?? [];
    group.push(entry);
    byPage.set(entry.page, group);
  }

  for (const group of byPage.values()) {
    for (const entry of group) {
      if (overlapping.has(entry.id) || !entry.tooltipRect) continue;

      for (const other of group) {
        if (entry === other) continue;

        if (
          other.tooltipRect &&
          rectsOverlap(entry.tooltipRect, other.tooltipRect)
        ) {
          overlapping.add(entry.id);
          overlapping.add(other.id);
          break;
        }
        if (rectsOverlap(entry.tooltipRect, other.boxRect)) {
          overlapping.add(entry.id);
          break;
        }
      }
    }
  }

  return overlapping;
};
